import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexto/AuthContexto';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Settings, Save, Upload, Trash2, ImageIcon, Percent, KeyRound, Globe2, Printer } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Configuracion() {
  const { usuario, refrescarUsuario } = useAuth();
  const esCarwash = usuario?.tipo_negocio === 'carwash';
  const [config, setConfig] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [subiendoLogo, setSubiendoLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [monedas, setMonedas] = useState([]);
  const [monedaActual, setMonedaActual] = useState('MXN');
  const [guardandoMoneda, setGuardandoMoneda] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const cargar = async () => {
      try {
        const [cRes, mRes] = await Promise.all([
          api.get('/configuracion'),
          api.get('/monedas'),
        ]);
        setConfig(cRes.data);
        setMonedas(mRes.data || []);
        setMonedaActual(cRes.data?.moneda || usuario?.moneda?.codigo || 'MXN');
        if (cRes.data.logo_url) {
          setLogoPreview(`${API_URL}${cRes.data.logo_url}?t=${Date.now()}`);
        }
      } catch { toast.error('Error al cargar configuracion'); }
      finally { setCargando(false); }
    };
    cargar();
  }, [usuario?.moneda?.codigo]);

  const cambiarMoneda = async (nuevaMoneda) => {
    if (nuevaMoneda === monedaActual) return;
    if (!window.confirm(
      `Cambiar la moneda a ${nuevaMoneda}? Esto afecta el simbolo mostrado en toda la app (tickets, reportes, ordenes) pero NO convierte los valores historicos.`
    )) return;
    setGuardandoMoneda(true);
    try {
      const r = await api.patch('/mi-tenant/moneda', { moneda: nuevaMoneda });
      // Persistir en localStorage para que formatoMoneda tome efecto inmediato
      localStorage.setItem('tenant_moneda', JSON.stringify({
        codigo: r.data.codigo || nuevaMoneda,
        simbolo: r.data.simbolo,
        locale: r.data.locale,
        decimales: r.data.decimales,
      }));
      setMonedaActual(nuevaMoneda);
      toast.success(`Moneda actualizada a ${nuevaMoneda} (${r.data.simbolo}). Recarga la pagina para ver el cambio completo.`);
      // Refrescar usuario para propagar el cambio en el AuthContext
      await refrescarUsuario();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cambiar moneda');
    } finally { setGuardandoMoneda(false); }
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const res = await api.put('/configuracion', config);
      if (res.data?.nuevo_token) {
        await refrescarUsuario(res.data.nuevo_token);
      }
      toast.success('Configuracion guardada. Se actualizo en todo el sistema.');
    } catch { toast.error('Error al guardar'); }
    finally { setGuardando(false); }
  };

  const subirLogo = async (e) => {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    if (!archivo.type.startsWith('image/')) {
      toast.error('Solo se permiten imagenes');
      return;
    }
    if (archivo.size > 5 * 1024 * 1024) {
      toast.error('Maximo 5MB');
      return;
    }
    setSubiendoLogo(true);
    const formData = new FormData();
    formData.append('archivo', archivo);
    try {
      const res = await api.post('/configuracion/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setConfig(prev => ({ ...prev, logo_url: res.data.logo_url }));
      setLogoPreview(`${API_URL}${res.data.logo_url}?t=${Date.now()}`);
      toast.success('Logo subido exitosamente');
    } catch { toast.error('Error al subir logo'); }
    finally { setSubiendoLogo(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const quitarLogo = async () => {
    try {
      await api.put('/configuracion', { ...config, logo_url: '' });
      setConfig(prev => ({ ...prev, logo_url: '' }));
      setLogoPreview(null);
      toast.success('Logo eliminado');
    } catch { toast.error('Error al eliminar logo'); }
  };

  const set = (campo, valor) => setConfig(prev => ({ ...prev, [campo]: valor }));

  if (cargando) return <div className="space-y-4"><Skeleton className="h-10 w-64 bg-slate-800" /><Skeleton className="h-48 bg-slate-800" /></div>;
  if (!config) return null;

  return (
    <div className="space-y-6 animate-fadeIn max-w-2xl" data-testid="configuracion-page">
      <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50 flex items-center gap-3">
        <Settings className={`w-8 h-8 ${esCarwash ? 'text-cyan-400' : 'text-amber-500'}`} /> {esCarwash ? 'Configuracion del Car Wash' : 'Configuracion del Taller'}
      </h1>

      {/* Logo */}
      <Card className="bg-slate-900/40 border-slate-800">
        <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Logo del {esCarwash ? 'Car Wash' : 'Taller'}</CardTitle></CardHeader>
        <CardContent>
          <p className="text-xs text-slate-500 mb-4">Este logo aparecera en las ordenes de trabajo y recibos impresos. Formatos: PNG, JPG, SVG. Maximo 5MB.</p>
          <div className="flex items-center gap-6">
            <div className="w-28 h-28 border-2 border-dashed border-slate-700 rounded-sm flex items-center justify-center overflow-hidden bg-slate-950 shrink-0"
              data-testid="logo-preview-container">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo" className="w-full h-full object-contain p-2" data-testid="logo-preview-img" />
              ) : (
                <ImageIcon className="w-10 h-10 text-slate-700" />
              )}
            </div>
            <div className="space-y-3">
              <input type="file" accept="image/*" ref={fileRef} onChange={subirLogo}
                className="hidden" data-testid="logo-file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={subiendoLogo}
                className={`${esCarwash ? 'bg-cyan-500 hover:bg-cyan-600' : 'bg-amber-500 hover:bg-amber-600'} text-black font-bold rounded-sm`}
                data-testid="subir-logo-btn">
                <Upload className="w-4 h-4 mr-2" />
                {subiendoLogo ? 'Subiendo...' : 'Subir Logo'}
              </Button>
              {logoPreview && (
                <Button variant="ghost" onClick={quitarLogo}
                  className="text-red-400 hover:text-red-300"
                  data-testid="quitar-logo-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> Quitar Logo
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Datos del taller */}
      <Card className="bg-slate-900/40 border-slate-800">
        <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Datos del {esCarwash ? 'Car Wash' : 'Taller'}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div><Label className="text-xs uppercase tracking-wider text-slate-400">Nombre del {esCarwash ? 'Car Wash' : 'Taller'}</Label>
            <Input value={config.nombre_taller || ''} onChange={e => set('nombre_taller', e.target.value)}
              className="bg-slate-950 border-slate-700 text-slate-200" data-testid="config-nombre-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Telefono</Label>
              <Input value={config.telefono || ''} onChange={e => set('telefono', e.target.value)}
                className="bg-slate-950 border-slate-700 text-slate-200" data-testid="config-telefono-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Email</Label>
              <Input value={config.email || ''} onChange={e => set('email', e.target.value)}
                className="bg-slate-950 border-slate-700 text-slate-200" data-testid="config-email-input" /></div>
          </div>
          <div><Label className="text-xs uppercase tracking-wider text-slate-400">Direccion</Label>
            <Input value={config.direccion || ''} onChange={e => set('direccion', e.target.value)}
              className="bg-slate-950 border-slate-700 text-slate-200" data-testid="config-direccion-input" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-slate-400">Horario de Atencion</Label>
            <Input value={config.horario || ''} onChange={e => set('horario', e.target.value)}
              className="bg-slate-950 border-slate-700 text-slate-200" data-testid="config-horario-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">RFC / ID Fiscal</Label>
              <Input value={config.rfc || ''} onChange={e => set('rfc', e.target.value)}
                className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="config-rfc-input" /></div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Moneda del Sistema</Label>
              <select
                value={monedaActual}
                onChange={e => cambiarMoneda(e.target.value)}
                disabled={guardandoMoneda}
                className="w-full h-10 rounded-md border border-slate-700 bg-slate-950 px-3 text-sm text-slate-200 font-mono mt-0.5 disabled:opacity-50"
                data-testid="config-moneda-select"
              >
                {monedas.map(m => (
                  <option key={m.codigo} value={m.codigo}>
                    {m.simbolo}  {m.codigo}  ·  {m.nombre}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-slate-500 mt-1">
                Cambio inmediato en tickets, reportes, ordenes y todo el sistema.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Descuento cliente frecuente - solo carwash */}
      {esCarwash && (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200 flex items-center gap-2"><Percent className="w-5 h-5 text-cyan-400" /> Descuento Cliente Frecuente</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-slate-500">Configura el descuento automatico para clientes que visitan frecuentemente tu car wash. Se detecta por placa del vehiculo.</p>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={config.descuento_frecuente_activo || false}
                  onChange={e => set('descuento_frecuente_activo', e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 accent-cyan-500" data-testid="config-descuento-activo" />
                <span className="text-sm text-slate-300">Activar descuento por cliente frecuente</span>
              </label>
            </div>
            {config.descuento_frecuente_activo && (
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs uppercase tracking-wider text-slate-400">Porcentaje de Descuento (%)</Label>
                  <Input type="number" min="1" max="100" value={config.descuento_frecuente_porcentaje || ''} onChange={e => set('descuento_frecuente_porcentaje', e.target.value)}
                    placeholder="10" className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="config-descuento-porcentaje" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-slate-400">Visitas Minimas</Label>
                  <Input type="number" min="1" max="100" value={config.descuento_frecuente_visitas_min || ''} onChange={e => set('descuento_frecuente_visitas_min', e.target.value)}
                    placeholder="5" className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="config-descuento-visitas" /></div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Programa de Lealtad por Visitas — solo carwash */}
      {esCarwash && (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200 flex items-center gap-2"><Percent className="w-5 h-5 text-amber-400" /> Programa de Lealtad por Visitas</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-slate-500">Cuando el cliente complete X visitas, el sistema aplica automaticamente el premio. El ticket muestra "Visita N de X" para motivar al cliente.</p>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={config.lealtad_activo || false}
                  onChange={e => set('lealtad_activo', e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 accent-amber-500" data-testid="config-lealtad-activo" />
                <span className="text-sm text-slate-300">Activar programa de lealtad</span>
              </label>
            </div>
            {config.lealtad_activo && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Visitas para premio</Label>
                    <Input type="number" min="2" max="50" value={config.lealtad_visitas_premio || ''}
                      onChange={e => set('lealtad_visitas_premio', e.target.value)}
                      placeholder="5" className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="config-lealtad-meta" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Tipo de premio</Label>
                    <select value={config.lealtad_tipo_premio || 'descuento'}
                      onChange={e => set('lealtad_tipo_premio', e.target.value)}
                      className="w-full h-10 bg-slate-950 border border-slate-700 text-slate-200 rounded-md px-3 text-sm" data-testid="config-lealtad-tipo">
                      <option value="descuento">Descuento %</option>
                      <option value="gratis">Lavado Gratis (100% descuento)</option>
                    </select>
                  </div>
                </div>
                {config.lealtad_tipo_premio !== 'gratis' && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">% de descuento del premio</Label>
                    <Input type="number" min="1" max="100" value={config.lealtad_descuento_pct || ''}
                      onChange={e => set('lealtad_descuento_pct', e.target.value)}
                      placeholder="50" className="bg-slate-950 border-slate-700 text-slate-200 font-mono w-32" data-testid="config-lealtad-pct" />
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Impuesto / IVA — solo taller */}
      {!esCarwash && (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200 flex items-center gap-2"><Percent className="w-5 h-5 text-amber-400" /> Impuesto sobre Ordenes</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-slate-500">
              Configura el impuesto que aplica en tu pais. Se calcula automaticamente cuando activas el toggle de impuesto en una orden.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Nombre del impuesto</Label>
                <Input value={config.nombre_impuesto || 'IVA'}
                  onChange={e => set('nombre_impuesto', e.target.value)}
                  placeholder="IVA, IGV, Sales Tax..." className="bg-slate-950 border-slate-700 text-slate-200"
                  data-testid="config-nombre-impuesto" />
                <p className="text-[10px] text-slate-500 mt-1">Ej: IVA (Mexico), IGV (Peru), ITBIS (RD)</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Tasa de impuesto (%)</Label>
                <Input type="number" step="0.01" min="0" max="50" value={config.tasa_impuesto ?? 16}
                  onChange={e => set('tasa_impuesto', parseFloat(e.target.value) || 0)}
                  placeholder="16" className="bg-slate-950 border-slate-700 text-slate-200 font-mono"
                  data-testid="config-tasa-impuesto" />
                <p className="text-[10px] text-slate-500 mt-1">Ej: 16 = 16% del subtotal</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {!esCarwash && (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Permisos de Mecanicos</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2 px-1 border border-slate-700 rounded-md cursor-pointer hover:bg-slate-800/30"
              onClick={() => set('mecanico_puede_agregar_insumos', !config.mecanico_puede_agregar_insumos)} data-testid="toggle-mecanico-insumos">
              <div>
                <span className="text-sm text-slate-200">Mecanicos pueden agregar insumos</span>
                <p className="text-[10px] text-slate-500 mt-0.5">Los mecanicos podran agregar refacciones a sus ordenes (sin precio). El administrativo asignara el precio despues.</p>
              </div>
              <div className={`w-10 h-5 rounded-full transition-colors relative shrink-0 ml-3 ${config.mecanico_puede_agregar_insumos ? 'bg-amber-500' : 'bg-slate-700'}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${config.mecanico_puede_agregar_insumos ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cambiar contraseña — aplica a taller y carwash */}
      <PasswordChangeCard esCarwash={esCarwash} />

      {/* Zona horaria — aplica a taller y carwash */}
      <Card className="bg-slate-900/40 border-slate-800">
        <CardHeader className="pb-3">
          <CardTitle className="font-barlow text-lg text-slate-200 flex items-center gap-2">
            <Globe2 className={`w-5 h-5 ${esCarwash ? 'text-cyan-400' : 'text-amber-400'}`} /> Zona Horaria
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-500">
            Selecciona la zona horaria de tu {esCarwash ? 'car wash' : 'taller'}. Se usa para mostrar la hora correcta en tickets, PDFs, cierres y reportes.
            En Mexico existen varias zonas: elige la de tu ciudad/estado.
          </p>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Zona Horaria (IANA)</Label>
            <select value={config.timezone || 'America/Mexico_City'}
              onChange={e => set('timezone', e.target.value)}
              className="w-full h-10 bg-slate-950 border border-slate-700 text-slate-200 rounded-md px-3 text-sm mt-1"
              data-testid="config-timezone-select">
              <optgroup label="Mexico">
                <option value="America/Mexico_City">Ciudad de Mexico (Centro) — America/Mexico_City</option>
                <option value="America/Cancun">Cancun / Quintana Roo — America/Cancun (UTC-5)</option>
                <option value="America/Merida">Yucatan — America/Merida</option>
                <option value="America/Monterrey">Monterrey / NL — America/Monterrey</option>
                <option value="America/Chihuahua">Chihuahua — America/Chihuahua</option>
                <option value="America/Ojinaga">Ojinaga — America/Ojinaga</option>
                <option value="America/Hermosillo">Hermosillo / Sonora — America/Hermosillo</option>
                <option value="America/Mazatlan">Mazatlan / Sinaloa — America/Mazatlan</option>
                <option value="America/Tijuana">Tijuana / BC — America/Tijuana</option>
                <option value="America/Bahia_Banderas">Bahia de Banderas — America/Bahia_Banderas</option>
                <option value="America/Matamoros">Matamoros — America/Matamoros</option>
              </optgroup>
              <optgroup label="Centroamerica y Caribe">
                <option value="America/Guatemala">Guatemala — America/Guatemala</option>
                <option value="America/El_Salvador">El Salvador — America/El_Salvador</option>
                <option value="America/Tegucigalpa">Honduras — America/Tegucigalpa</option>
                <option value="America/Managua">Nicaragua — America/Managua</option>
                <option value="America/Costa_Rica">Costa Rica — America/Costa_Rica</option>
                <option value="America/Panama">Panama — America/Panama</option>
                <option value="America/Havana">Cuba — America/Havana</option>
                <option value="America/Santo_Domingo">Rep. Dominicana — America/Santo_Domingo</option>
                <option value="America/Port-au-Prince">Haiti — America/Port-au-Prince</option>
              </optgroup>
              <optgroup label="Sudamerica">
                <option value="America/Bogota">Colombia — America/Bogota</option>
                <option value="America/Lima">Peru — America/Lima</option>
                <option value="America/Guayaquil">Ecuador — America/Guayaquil</option>
                <option value="America/Caracas">Venezuela — America/Caracas</option>
                <option value="America/La_Paz">Bolivia — America/La_Paz</option>
                <option value="America/Santiago">Chile — America/Santiago</option>
                <option value="America/Argentina/Buenos_Aires">Argentina (Buenos Aires) — America/Argentina/Buenos_Aires</option>
                <option value="America/Argentina/Cordoba">Argentina (Cordoba) — America/Argentina/Cordoba</option>
                <option value="America/Argentina/Mendoza">Argentina (Mendoza) — America/Argentina/Mendoza</option>
                <option value="America/Montevideo">Uruguay — America/Montevideo</option>
                <option value="America/Asuncion">Paraguay — America/Asuncion</option>
                <option value="America/Sao_Paulo">Brasil (Sao Paulo) — America/Sao_Paulo</option>
              </optgroup>
              <optgroup label="Estados Unidos">
                <option value="America/New_York">Nueva York — America/New_York</option>
                <option value="America/Chicago">Chicago — America/Chicago</option>
                <option value="America/Denver">Denver — America/Denver</option>
                <option value="America/Phoenix">Phoenix — America/Phoenix</option>
                <option value="America/Los_Angeles">Los Angeles — America/Los_Angeles</option>
              </optgroup>
              <optgroup label="Europa">
                <option value="Europe/Madrid">Madrid / Espana — Europe/Madrid</option>
              </optgroup>
            </select>
            <p className="text-[10px] text-slate-500 mt-1">Zona actual: <span className="font-mono text-cyan-400">{config.timezone || 'America/Mexico_City'}</span></p>
          </div>
        </CardContent>
      </Card>

      {/* Ticket térmico configurable */}
      <Card className="bg-slate-900/40 border-slate-800">
        <CardHeader className="pb-3">
          <CardTitle className="font-barlow text-lg text-slate-200 flex items-center gap-2">
            <Printer className={`w-5 h-5 ${esCarwash ? 'text-cyan-400' : 'text-amber-400'}`} /> Diseño del Ticket Térmico
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-slate-500">
            Personaliza qué información se imprime en el ticket térmico. Estos ajustes solo aplican al ticket térmico (impresora 58/80mm) — el ticket digital y PDF no se ven afectados.
          </p>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Ancho de papel</Label>
            <select value={config.ticket_ancho_mm || 80}
              onChange={e => set('ticket_ancho_mm', parseInt(e.target.value))}
              className="w-full h-10 bg-slate-950 border border-slate-700 text-slate-200 rounded-md px-3 text-sm mt-1"
              data-testid="config-ticket-ancho">
              <option value={58}>58 mm (impresoras compactas)</option>
              <option value={80}>80 mm (estándar POS)</option>
            </select>
          </div>

          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Mostrar / ocultar campos</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                ['ticket_mostrar_logo', 'Logo del negocio'],
                ['ticket_mostrar_direccion', 'Dirección'],
                ['ticket_mostrar_telefono', 'Teléfono'],
                ['ticket_mostrar_rfc', 'RFC'],
                ['ticket_mostrar_cliente', 'Nombre del cliente'],
                ['ticket_mostrar_placa', 'Placa del vehículo'],
                ['ticket_mostrar_operario', 'Operario asignado'],
                ['ticket_mostrar_metodo_pago', 'Método de pago'],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 px-3 py-2 bg-slate-950 border border-slate-800 rounded cursor-pointer hover:border-slate-700 text-sm">
                  <input type="checkbox"
                    checked={config[key] !== false}
                    onChange={e => set(key, e.target.checked)}
                    className={`w-4 h-4 ${esCarwash ? 'accent-cyan-500' : 'accent-amber-500'}`}
                    data-testid={`config-${key.replace(/_/g, '-')}`} />
                  <span className="text-slate-300">{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Mensaje al inicio del ticket (opcional)</Label>
            <Input value={config.ticket_mensaje_encabezado || ''}
              onChange={e => set('ticket_mensaje_encabezado', e.target.value)}
              placeholder="Ej. Sucursal centro · Facturación disponible"
              className="bg-slate-950 border-slate-700 text-slate-200"
              data-testid="config-ticket-encabezado" maxLength={120} />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Mensaje al pie del ticket</Label>
            <Input value={config.ticket_mensaje_pie || ''}
              onChange={e => set('ticket_mensaje_pie', e.target.value)}
              placeholder="Ej. Gracias por su preferencia — Vuelva pronto"
              className="bg-slate-950 border-slate-700 text-slate-200"
              data-testid="config-ticket-pie" maxLength={120} />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Términos y condiciones (opcional)</Label>
            <textarea value={config.ticket_terminos_condiciones || ''}
              onChange={e => set('ticket_terminos_condiciones', e.target.value)}
              placeholder="Ej. No nos hacemos responsables por objetos olvidados dentro del vehículo. Garantía de 48h en servicio de lavado..."
              className="w-full min-h-[80px] bg-slate-950 border border-slate-700 text-slate-200 rounded-md p-2 text-sm mt-1"
              data-testid="config-ticket-terminos" maxLength={600} rows={4} />
            <p className="text-[10px] text-slate-500 mt-1">Máximo 600 caracteres. Aparece al final del ticket en letra pequeña.</p>
          </div>
        </CardContent>
      </Card>

      <Button onClick={guardar} disabled={guardando} className={`${esCarwash ? 'bg-cyan-500 hover:bg-cyan-600' : 'bg-amber-500 hover:bg-amber-600'} text-black font-bold rounded-sm h-11 shadow-[0_0_15px_rgba(245,158,11,0.3)]`} data-testid="guardar-config-btn">
        <Save className="w-4 h-4 mr-2" />
        {guardando ? 'Guardando...' : 'Guardar Configuracion'}
      </Button>
    </div>
  );
}

function PasswordChangeCard({ esCarwash }) {
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [cargando, setCargando] = useState(false);
  const [mostrar, setMostrar] = useState(false);

  const cambiar = async () => {
    if (!actual || !nueva) { toast.error('Completa todos los campos'); return; }
    if (nueva.length < 6) { toast.error('La nueva contrasena debe tener al menos 6 caracteres'); return; }
    if (nueva !== confirmar) { toast.error('Las contrasenas no coinciden'); return; }
    setCargando(true);
    try {
      await api.put('/auth/cambiar-password', { password_actual: actual, password_nuevo: nueva });
      toast.success('Contrasena actualizada');
      setActual(''); setNueva(''); setConfirmar('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cambiar contrasena'); }
    finally { setCargando(false); }
  };

  return (
    <Card className="bg-slate-900/40 border-slate-800">
      <CardHeader className="pb-3">
        <CardTitle className="font-barlow text-lg text-slate-200 flex items-center gap-2">
          <KeyRound className={`w-5 h-5 ${esCarwash ? 'text-cyan-400' : 'text-amber-400'}`} /> Cambiar Contrasena
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-500">Cambia tu contrasena de acceso al sistema. Se requiere tu contrasena actual para confirmar.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Contrasena actual</Label>
            <Input type={mostrar ? 'text' : 'password'} value={actual} onChange={e => setActual(e.target.value)}
              className="bg-slate-950 border-slate-700 text-slate-200" data-testid="password-actual-input" autoComplete="current-password" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Nueva contrasena</Label>
            <Input type={mostrar ? 'text' : 'password'} value={nueva} onChange={e => setNueva(e.target.value)}
              className="bg-slate-950 border-slate-700 text-slate-200" data-testid="password-nueva-input" autoComplete="new-password" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Confirmar</Label>
            <Input type={mostrar ? 'text' : 'password'} value={confirmar} onChange={e => setConfirmar(e.target.value)}
              className="bg-slate-950 border-slate-700 text-slate-200" data-testid="password-confirmar-input" autoComplete="new-password" />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
            <input type="checkbox" checked={mostrar} onChange={e => setMostrar(e.target.checked)} className="accent-cyan-500" />
            Mostrar contrasenas
          </label>
          <Button onClick={cambiar} disabled={cargando} size="sm"
            className={`${esCarwash ? 'bg-cyan-500 hover:bg-cyan-600' : 'bg-amber-500 hover:bg-amber-600'} text-black font-bold`}
            data-testid="cambiar-password-btn">
            {cargando ? 'Cambiando...' : 'Actualizar contrasena'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
